import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDiaryContext } from "../../../../../../hooks/useDiaryContext";
import { useAppContext } from "../../../../../../hooks/useAppContext";
import { useNotificationsContext } from "../../../../../../hooks/useNotificationsContext";
import { useBookingPermissions } from "../../../../../../hooks/useBookingPermissions";
import { postUpdateBookingV4 } from "../../../../../../APIs/Booking/UpdateBooking";
import { BookingV4 } from "../../../../../../types/bookings";
import { CollapsibleSection } from "../../../../../blocks/CollapsibleSection";
import { Input } from "../../../../../ui/input";
import { Label } from "../../../../../ui/label";
import { getCurrencySymbol } from "../../../../../../utils/number";

const DEBOUNCE_DELAY = 300;

const AdditionalDetails = () => {
  const { selectedBooking, selectedBookingV4, venueConfig, setDiaryContext } =
    useDiaryContext();
  const { venueGroupConfig } = useAppContext();
  const notificationsContext = useNotificationsContext();
  const { userCanManageBooking: canEdit } = useBookingPermissions();

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedBookingV4Ref = useRef(selectedBookingV4);
  const isSavingRef = useRef(false);

  const currencySymbol = getCurrencySymbol(venueConfig?.currency ?? "GBP");
  const currencyCode = venueConfig?.currency ?? "GBP";

  const [localValues, setLocalValues] = useState<Record<string, string>>({
    budget: selectedBookingV4?.budget?.toString() ?? "",
    approximate_guests: selectedBookingV4?.approximate_guests ?? "",
    for_name: selectedBookingV4?.for_name ?? "",
    custom_field_value: selectedBookingV4?.custom_field_value ?? "",
    external_ref: selectedBookingV4?.external_ref ?? "",
    commission_amount: selectedBookingV4?.commission_amount ?? ""
  });

  const customFieldLabel = useMemo(() => {
    return (
      selectedBookingV4?.custom_field_label ??
      venueGroupConfig?.booking_custom_field_label ??
      ""
    );
  }, [
    selectedBookingV4?.custom_field_label,
    venueGroupConfig?.booking_custom_field_label
  ]);

  useEffect(() => {
    selectedBookingV4Ref.current = selectedBookingV4;
    if (isSavingRef.current || debounceTimer.current) return;

    setLocalValues({
      budget: selectedBookingV4?.budget?.toString() ?? "",
      approximate_guests: selectedBookingV4?.approximate_guests ?? "",
      for_name: selectedBookingV4?.for_name ?? "",
      custom_field_value: selectedBookingV4?.custom_field_value ?? "",
      external_ref: selectedBookingV4?.external_ref ?? "",
      commission_amount: selectedBookingV4?.commission_amount ?? ""
    });
  }, [selectedBookingV4, selectedBooking]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const saveField = useCallback(
    async (updates: Partial<BookingV4>) => {
      const currentBooking = selectedBookingV4Ref.current;
      if (!currentBooking?._id) return;

      isSavingRef.current = true;

      const bookingPayload = {
        _id: currentBooking._id,
        ...updates
      };

      try {
        const response = await postUpdateBookingV4(bookingPayload as BookingV4);

        if (!response) {
          notificationsContext.addNotification({
            heading: "Failed to update additional details",
            autoClose: 4000,
            toastType: "error"
          });
          return;
        }

        setDiaryContext({
          selectedBookingV4: {
            ...currentBooking,
            ...response
          }
        });
      } catch {
        notificationsContext.addNotification({
          heading: "Failed to update additional details",
          autoClose: 4000,
          toastType: "error"
        });
      } finally {
        isSavingRef.current = false;
      }
    },
    [setDiaryContext, notificationsContext]
  );

  const debouncedSave = useCallback(
    (updates: Partial<BookingV4>) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null;
        saveField(updates);
      }, DEBOUNCE_DELAY);
    },
    [saveField]
  );

  const handleFieldChange = useCallback(
    (field: string, value: string) => {
      setLocalValues((prev) => ({ ...prev, [field]: value }));
      if (field === "budget") {
        const parsed = parseFloat(value);
        debouncedSave({ budget: isNaN(parsed) ? undefined : parsed });
      } else {
        debouncedSave({ [field]: value });
      }
    },
    [debouncedSave]
  );

  const filledCount = useMemo(
    () => Object.values(localValues).filter(Boolean).length,
    [localValues]
  );

  const summary = useMemo(() => {
    const parts: string[] = [];
    if (localValues.budget)
      parts.push(`Budget: ${currencySymbol}${localValues.budget}`);
    if (localValues.for_name) parts.push(`For: ${localValues.for_name}`);
    if (localValues.approximate_guests)
      parts.push(`~${localValues.approximate_guests} guests`);
    if (parts.length === 0) return "No details";
    return parts.slice(0, 2).join(" \u00b7 ");
  }, [localValues, currencySymbol]);

  if (selectedBooking && !selectedBookingV4) {
    return null;
  }

  if (!selectedBookingV4) {
    return null;
  }

  return (
    <CollapsibleSection
      title="Additional Details"
      badge={String(filledCount)}
      summary={summary}
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="additional-budget">Budget</Label>
            <div className="relative">
              <span
                className="absolute left-[0.625em] top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none z-[1]"
                aria-hidden="true"
              >
                {currencySymbol}
              </span>
              <Input
                id="additional-budget"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={localValues.budget}
                onChange={(e) => handleFieldChange("budget", e.target.value)}
                disabled={!canEdit}
                placeholder="0"
                className="h-9 text-sm pl-[1.75em] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0"
                aria-label={`Budget in ${currencyCode}`}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="additional-approx-guests">Approximate guests</Label>
            <Input
              id="additional-approx-guests"
              type="text"
              value={localValues.approximate_guests}
              onChange={(e) =>
                handleFieldChange("approximate_guests", e.target.value)
              }
              disabled={!canEdit}
              className="h-9 text-sm"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="additional-for-name">Booking for</Label>
          <Input
            id="additional-for-name"
            type="text"
            value={localValues.for_name}
            onChange={(e) => handleFieldChange("for_name", e.target.value)}
            disabled={!canEdit}
            className="h-9 text-sm"
          />
        </div>

        {customFieldLabel && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="additional-custom-field">{customFieldLabel}</Label>
            <Input
              id="additional-custom-field"
              type="text"
              value={localValues.custom_field_value}
              onChange={(e) =>
                handleFieldChange("custom_field_value", e.target.value)
              }
              disabled={!canEdit}
              className="h-9 text-sm"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="additional-external-ref">Reference code</Label>
            <Input
              id="additional-external-ref"
              type="text"
              value={localValues.external_ref}
              onChange={(e) =>
                handleFieldChange("external_ref", e.target.value)
              }
              disabled={!canEdit}
              className="h-9 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="additional-commission">Commission</Label>
            <Input
              id="additional-commission"
              type="text"
              value={localValues.commission_amount}
              onChange={(e) =>
                handleFieldChange("commission_amount", e.target.value)
              }
              disabled={!canEdit}
              className="h-9 text-sm"
            />
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};

export default AdditionalDetails;
